'use client';

import { Component, type ReactNode } from 'react';

// The server logger is for real users' failures. Automated runtimes (headless
// scrapers executing React, jsdom-like engines) have no SVG layout API and
// produce hundreds of reports a day that carry no information.
function isNonBrowserRuntime(): boolean {
  try {
    return (
      typeof window === 'undefined' ||
      navigator.webdriver === true ||
      typeof SVGGraphicsElement === 'undefined' ||
      typeof SVGGraphicsElement.prototype.getBBox !== 'function'
    );
  } catch {
    return false;
  }
}

interface Props {
  /** Tag included in client console logs so we can grep where it tripped. */
  label?: string;
  /** Rendered in place of the crashed subtree. */
  fallback: ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Subtree-scoped error boundary. A throw inside `children` is replaced with
 * `fallback` instead of taking the whole page down (and locking the cookie
 * banner / lazy chunks behind a hung React tree).
 *
 * Client-only on purpose — React's server renderer surfaces server errors
 * via the route-level error.tsx file; this boundary catches the hydration +
 * post-mount render path where most data-shape failures show up.
 */
export class RenderErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Keep the DevTools log for local debugging.
    console.error('[RenderErrorBoundary]', this.props.label ?? 'unknown', {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });

    if (isNonBrowserRuntime()) return;

    // Beacon the failure to the server logger so Loki/Grafana picks it up.
    // Fire-and-forget: keepalive=true lets the request survive even if the
    // user navigates away on a crashed page; we ignore the response.
    try {
      void fetch('/api/log/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          label: this.props.label,
          message: error.message,
          stack: error.stack,
          componentStack: info.componentStack,
          url:
            typeof window !== 'undefined'
              ? window.location.href
              : undefined,
        }),
      }).catch(() => {});
    } catch {
      // Defensive: if even constructing the beacon throws (e.g. JSON.stringify
      // on a circular structure), don't compound the original error.
    }
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}
